import { Component, OnInit } from '@angular/core';
import { CommonModule, Location } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatCardModule } from '@angular/material/card';
import { MatDividerModule } from '@angular/material/divider';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { ReceiptDto, PaymentMethodDto, PaymentMethodType, PaymentType } from '../../../domain/models/payment.model';
import { CasheringService } from '../../../domain/services/cashering/cashering.service';

@Component({
  selector: 'app-receipt',
  standalone: true,
  imports: [
    CommonModule,
    MatButtonModule,
    MatIconModule,
    MatCardModule,
    MatDividerModule,
    MatProgressSpinnerModule
  ],
  templateUrl: './receipt.component.html',
  styleUrls: ['./receipt.component.css']
})
export class ReceiptComponent implements OnInit {
  customerId!: number;
  // receiptId holds the numeric payment ID as a string from the route param
  receiptId!: string;
  receipt: ReceiptDto | null = null;
  isLoading: boolean = true;
  loadError: string | null = null;

  PaymentMethodType = PaymentMethodType;
  PaymentType = PaymentType;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private location: Location,
    private casheringService: CasheringService
  ) {}

  /** Return to wherever the receipt was opened from (e.g. the customer Receipts tab). */
  goBack(): void {
    this.location.back();
  }

  ngOnInit(): void {
    this.route.params.subscribe(params => {
      this.customerId = +params['customerId'];
      this.receiptId = params['receiptId'];
      this.loadReceiptData();
    });
  }

  loadReceiptData(): void {
    const paymentId = +this.receiptId;
    if (!paymentId || isNaN(paymentId)) {
      this.loadError = 'Invalid payment ID.';
      this.isLoading = false;
      return;
    }

    this.isLoading = true;
    this.loadError = null;

    // Call GET /api/payments/{paymentId}/receipt
    this.casheringService.getReceipt(paymentId).subscribe({
      next: (receipt: ReceiptDto) => {
        this.receipt = receipt;
        this.isLoading = false;
      },
      error: (error: any) => {
        console.error('Error loading receipt:', error);
        this.loadError = 'Failed to load receipt. Please try again.';
        this.isLoading = false;
      }
    });
  }

  /** Fees not shown on their own line (lien, attorney, custom); keeps the lines adding up. */
  get otherFees(): number {
    const r = this.receipt;
    if (!r || r.lateFee == null) { return 0; }
    const other = (r.totalFees ?? 0) - r.lateFee - (r.lateInterest ?? 0) - (r.nsfFee ?? 0);
    return Math.round(other * 100) / 100;
  }

  getLateFee(): number {
    const lateFeeMethods = this.receipt?.paymentMethods?.filter(m => m.type === PaymentMethodType.DEBIT_CARD);
    return lateFeeMethods?.reduce((sum, m) => sum + (m.cardFeeAmount || 0), 0) || 0;
  }

  /** Total charged to the customer (principal + any processor-retained card fee). */
  getTotalPaid(): number {
    if (!this.receipt?.paymentMethods) return 0;
    return this.receipt.paymentMethods.reduce((sum, m) => {
      return sum + m.amount + (m.cardFeeAmount || 0);
    }, 0);
  }

  /** True when a gateway-verified card fee is present, i.e. what the customer was charged
   *  (getTotalPaid) exceeds what the county actually collected (receipt.totalPayment) — the
   *  reconciliation document needs both figures visible, not just the customer-facing one. */
  hasProcessorFee(): boolean {
    return (this.receipt?.paymentMethods || []).some(m => (m.cardFeeAmount || 0) > 0);
  }

  /**
   * Cash change handed back to the customer on overpayment (not a drawer over/short).
   * Card fees are excluded — only the method principals beyond the amount due are change,
   * and in practice that overpayment is cash.
   */
  getCashChangeDue(): number {
    const principal = (this.receipt?.paymentMethods || [])
      .reduce((sum, m) => sum + m.amount, 0);
    const change = principal - (this.receipt?.totalAmountDue || 0);
    return change > 0 ? change : 0;
  }

  getPaymentMethodLabel(method: PaymentMethodDto): string {
    switch (method.type) {
      case PaymentMethodType.DEBIT_CARD:
        return `Debit Card - ${method.cardLastFour} - ${method.cardType || 'Card'}`;
      case PaymentMethodType.CREDIT_CARD:
        return `Credit Card - ${method.cardLastFour} - ${method.cardType || 'Card'}`;
      case PaymentMethodType.CASH:
        return 'Cash';
      case PaymentMethodType.CHECK:
        return `Check - ${method.checkNumber || ''}`;
      default:
        return method.type;
    }
  }

  getPaymentTypeLabel(): string {
    return this.receipt?.paymentType === PaymentType.WALK_IN ? 'Walk-in' : 'Mail-in';
  }

  getCashierInitials(): string {
    const name = this.receipt?.cashierName || '';
    return name.split(' ').map((n: string) => n[0]).join('').toUpperCase().slice(0, 3);
  }

  getPrintedDate(): string {
    return new Date(this.receipt?.printedAt || Date.now()).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  printReceipt(): void {
    window.print();
  }

  goToPayments(): void {
    this.router.navigate(['/app/cashering', this.customerId]);
  }
}
